import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Copy, Check, Sparkles, Monitor, Tablet, Smartphone, Tv,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SearchableDropdown from '../SearchableDropdown';

import { RubyGemMark } from '../RubyGemMark';
/**
 * Provision Kiosk modal.
 *
 * Mirrors ProvisionDisplayModal (advertising) but type-aware: the per-type
 * config form is driven off a small local registry; adding a new kiosk type
 * = add an entry here + the matching shared-types registry entry.
 *
 * Layout:
 *   1. Type picker
 *   2. Identity   (name, description)
 *   3. Location   (existing tenant locations dropdown)
 *   4. Display    (orientation big-button toggle + screen size + resolution)
 *   5. Idle mode  (playlist + delay + resume window)
 *   6. Per-type   (oil-blending → allowBuildYourOwn). Submissions broadcast
 *                  to every register at the chosen location via WebSocket;
 *                  first to Claim wins. No per-kiosk register link.
 *   7. Success    (shows KIOSK-XXXX-XXXX big, with Copy)
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onProvisioned: () => void;
  tenantId: string;
}

interface KioskTypeOption {
  id: string;
  label: string;
  icon: React.FC<any>;
  description: string;
}

const KIOSK_TYPE_OPTIONS: KioskTypeOption[] = [
  {
    id: 'oil-blending',
    label: 'Oil Blending Kiosk',
    icon: Sparkles,
    description: 'Customer self-serve build/browse blend kiosk',
  },
  // Future: feedback, item-request, etc.
];

const SCREEN_SIZE_PRESETS = [
  { value: 7, label: '7″ small tablet / phone' },
  { value: 8, label: '8″ tablet' },
  { value: 10.9, label: '10.9″ iPad / Android tablet' },
  { value: 11, label: '11″ iPad Pro' },
  { value: 12.9, label: '12.9″ iPad Pro' },
  { value: 13, label: '13″ tablet / laptop' },
  { value: 24, label: '24″ monitor' },
  { value: 32, label: '32″ display / TV' },
  { value: 43, label: '43″ TV' },
  { value: 55, label: '55″ TV' },
  { value: 65, label: '65″ TV' },
];

interface LocationOption {
  locationId: string;
  name: string;
}

interface PlaylistOption {
  playlistId: string;
  name: string;
}

const ProvisionKioskModal: React.FC<Props> = ({ isOpen, onClose, onProvisioned, tenantId }) => {
  // ── form state ──────────────────────────────────────────────
  const [kioskType, setKioskType] = useState<string>('oil-blending');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locationId, setLocationId] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('portrait');
  const [screenSizeInches, setScreenSizeInches] = useState<number | ''>(10.9);
  const [resolution, setResolution] = useState<'tablet' | '1080p' | '4k'>('tablet');
  const [idleAfterSeconds, setIdleAfterSeconds] = useState(60);
  const [resumeWindowMinutes, setResumeWindowMinutes] = useState(5);
  const [idlePlaylistId, setIdlePlaylistId] = useState<string>('');
  // oil-blending config
  const [allowBuildYourOwn, setAllowBuildYourOwn] = useState(true);

  // ── lookup data ─────────────────────────────────────────────
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [playlists, setPlaylists] = useState<PlaylistOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // ── submit state ────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ provisionCode: string; kioskId: string } | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  /* ── load lookups when modal opens ───────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLookupsLoading(true);
      try {
        const locsResp: any = await (api as any).locationBilling
          .listTenantRequests(tenantId)
          .catch(() => ({ requests: [] }));
        if (cancelled) return;

        // Locations come from location-billing requests (same source as AdvertisingTab).
        const locArr: any[] = locsResp.requests || locsResp.locations || [];
        setLocations(
          locArr
            .filter((l) => l?.locationId)
            .map((l: any) => ({
              locationId: l.locationId,
              name: l.name || l.legalEntityName || l.locationId,
            })),
        );

        // Playlists: no super-admin endpoint yet — leave empty so admin can
        // assign one later from the kiosk detail page. Still safe — backend
        // accepts idlePlaylistId=null (falls back to branded loop).
        setPlaylists([]);
      } finally {
        if (!cancelled) setLookupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, tenantId]);

  /* ── reset state on close ────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) {
      setKioskType('oil-blending');
      setName('');
      setDescription('');
      setLocationId('');
      setOrientation('portrait');
      setScreenSizeInches(10.9);
      setResolution('tablet');
      setIdleAfterSeconds(60);
      setResumeWindowMinutes(5);
      setIdlePlaylistId('');
      setAllowBuildYourOwn(true);
      setResult(null);
      setCodeCopied(false);
    }
  }, [isOpen]);

  const canSubmit = useMemo(() => {
    if (!kioskType || !name.trim() || !locationId) return false;
    return true;
  }, [kioskType, name, locationId]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const config =
        kioskType === 'oil-blending'
          ? { allowBuildYourOwn }
          : {};
      const resp: any = await api.kiosks.provision({
        tenantId,
        locationId,
        kioskType,
        name: name.trim(),
        description: description.trim() || undefined,
        idlePlaylistId: idlePlaylistId || null,
        idleAfterSeconds,
        resumeWindowMinutes,
        resolution,
        orientation,
        screenSizeInches: typeof screenSizeInches === 'number' ? screenSizeInches : undefined,
        config,
      });
      const provisionCode = resp.provisionCode || resp.data?.provisionCode;
      const kioskId = resp.kioskId || resp.data?.kioskId;
      if (!provisionCode || !kioskId) throw new Error('No code returned');
      setResult({ provisionCode, kioskId });
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to provision kiosk');
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit, submitting, kioskType, tenantId, locationId, name, description,
    idlePlaylistId, idleAfterSeconds, resumeWindowMinutes, resolution, orientation,
    screenSizeInches, allowBuildYourOwn,
  ]);

  const handleCopyCode = useCallback(async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.provisionCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  }, [result]);

  const handleDone = useCallback(() => {
    onProvisioned();
    onClose();
  }, [onProvisioned, onClose]);

  if (!isOpen) return null;

  /* ── render ─────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white text-stone-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-black text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
            <Monitor size={20} />
            {result ? 'Kiosk Provisioned' : 'Provision New Kiosk'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Success state */}
        {result && (
          <div className="p-8 space-y-6">
            <p className="text-stone-600">
              Your kiosk is ready. Open the storefront on the device and enter this code on the pair
              screen:
            </p>
            <div className="bg-stone-50 border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
              <div className="text-3xl md:text-4xl font-mono font-bold tracking-wider text-stone-900 mb-3">
                {result.provisionCode}
              </div>
              <button
                onClick={handleCopyCode}
                className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
              >
                {codeCopied ? <Check size={14} /> : <Copy size={14} />}
                {codeCopied ? 'Copied' : 'Copy code'}
              </button>
            </div>
            <p className="text-xs text-stone-500">
              The code stays valid until you release or delete this kiosk. If the device is replaced
              you can re-enter the same code.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={handleDone}
                className="bg-black text-white px-4 py-2 rounded text-sm font-medium hover:bg-stone-800"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Form state */}
        {!result && (
          <div className="p-6 space-y-5">
            {/* Kiosk type */}
            <Section title="Kiosk Type">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {KIOSK_TYPE_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  const active = kioskType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setKioskType(t.id)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                        active
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-stone-300 hover:border-stone-400'
                      }`}
                    >
                      <Icon size={18} className={active ? 'text-emerald-600' : 'text-stone-600'} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{t.label}</div>
                        <div className="text-xs text-stone-500 truncate">{t.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* Identity */}
            <Section title="Identity">
              <FieldRow label="Name *" hint="Admin label, never shown to customers">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Front Counter Kiosk"
                  className="w-full px-3 py-2 text-sm text-stone-900 bg-white border border-stone-300 rounded focus:outline-none focus:border-emerald-600 placeholder:text-stone-400"
                />
              </FieldRow>
              <FieldRow label="Description">
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 text-sm text-stone-900 bg-white border border-stone-300 rounded focus:outline-none focus:border-emerald-600 placeholder:text-stone-400"
                />
              </FieldRow>
            </Section>

            {/* Location */}
            <Section title="Location">
              <FieldRow label="Store location *">
                <SearchableDropdown
                  options={locations.map((l) => ({ value: l.locationId, label: l.name }))}
                  value={locationId || null}
                  onChange={(v) => setLocationId(v || '')}
                  placeholder={lookupsLoading ? 'Loading…' : 'Pick a location'}
                  disabled={lookupsLoading}
                />
              </FieldRow>
            </Section>

            {/* Display */}
            <Section title="Display Setup">
              <FieldRow label="Orientation">
                <div className="grid grid-cols-2 gap-2">
                  <OrientationBtn
                    active={orientation === 'portrait'}
                    onClick={() => setOrientation('portrait')}
                    icon={<Smartphone size={20} />}
                    label="Portrait"
                  />
                  <OrientationBtn
                    active={orientation === 'landscape'}
                    onClick={() => setOrientation('landscape')}
                    icon={<Tv size={20} className="rotate-0" />}
                    label="Landscape"
                  />
                </div>
              </FieldRow>
              <FieldRow label="Screen size">
                <SearchableDropdown
                  options={SCREEN_SIZE_PRESETS.map((s) => ({ value: String(s.value), label: s.label }))}
                  value={screenSizeInches === '' ? null : String(screenSizeInches)}
                  onChange={(v) => setScreenSizeInches(v === null || v === '' ? '' : parseFloat(v))}
                  placeholder="Not set"
                />
              </FieldRow>
              <FieldRow label="Resolution">
                <SearchableDropdown
                  options={[
                    { value: 'tablet', label: 'Tablet (native)' },
                    { value: '1080p', label: '1080p' },
                    { value: '4k', label: '4K' },
                  ]}
                  value={resolution}
                  onChange={(v) => setResolution((v || 'tablet') as any)}
                  placeholder="Pick resolution"
                />
              </FieldRow>
            </Section>

            {/* Idle mode */}
            <Section title="Idle Mode">
              <FieldRow label="Idle playlist" hint="Plays when no touch — assign one later if you like">
                <SearchableDropdown
                  options={[
                    { value: '__none', label: 'None (branded fallback loop)' },
                    ...playlists.map((p) => ({ value: p.playlistId, label: p.name })),
                  ]}
                  value={idlePlaylistId || '__none'}
                  onChange={(v) => setIdlePlaylistId(v === '__none' || v === null ? '' : v)}
                  placeholder="Pick a playlist"
                />
              </FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label={`Idle delay: ${idleAfterSeconds}s`}>
                  <input
                    type="range" min={15} max={300} step={5}
                    value={idleAfterSeconds}
                    onChange={(e) => setIdleAfterSeconds(parseInt(e.target.value, 10))}
                    className="w-full"
                  />
                </FieldRow>
                <FieldRow label={`Resume window: ${resumeWindowMinutes}min`}>
                  <input
                    type="range" min={0} max={30} step={1}
                    value={resumeWindowMinutes}
                    onChange={(e) => setResumeWindowMinutes(parseInt(e.target.value, 10))}
                    className="w-full"
                  />
                </FieldRow>
              </div>
            </Section>

            {/* Per-type config: oil-blending */}
            {kioskType === 'oil-blending' && (
              <Section title="Oil Blending Settings">
                <div className="text-xs text-stone-500 -mt-1 mb-1">
                  Submissions broadcast to every POS register at the chosen location.
                  First register to claim wins.
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-700 pt-1">
                  <input
                    type="checkbox"
                    checked={allowBuildYourOwn}
                    onChange={(e) => setAllowBuildYourOwn(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  Allow customers to build their own blend (uncheck for signature-only)
                </label>
              </Section>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded text-sm font-medium text-stone-600 hover:bg-stone-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="bg-black text-white px-4 py-2 rounded text-sm font-medium hover:bg-stone-800 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {submitting && <RubyGemMark size={14} drawing color="currentColor" />}
                Provision Kiosk
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── tiny presentational helpers (kept local to avoid cross-file deps) ──── */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const FieldRow: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-sm text-stone-700">{label}</span>
      {hint && <span className="text-xs text-stone-400">{hint}</span>}
    </div>
    {children}
  </label>
);

const OrientationBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-lg border transition ${
      active ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-stone-300 hover:border-stone-400 text-stone-600'
    }`}
  >
    {icon}
    <span className="text-sm font-medium">{label}</span>
  </button>
);

// Silence unused-icon imports if not used in current branches.
void Tablet;

export default ProvisionKioskModal;
